import type { ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'

import { SessionProvider } from '@/features/auth/session'
import { RequireAdmin } from '@/features/auth/guards'
import { LoginPage } from '@/routes/login'
import { NotFoundPage } from '@/routes/not-found'
import { DashboardLayout } from '@/routes/dashboard/layout'
import { DashboardHome } from '@/routes/dashboard'
import { NoticiasListPage } from '@/routes/dashboard/noticias'
import { NoticiaNewPage } from '@/routes/dashboard/noticias/new'
import { NoticiaEditPage } from '@/routes/dashboard/noticias/[id]'
import { UsuariosListPage } from '@/routes/dashboard/usuarios'
import { FichasListPage } from '@/routes/dashboard/fichas'
import { FichaNewPage } from '@/routes/dashboard/fichas/new'
import { FichaEditPage } from '@/routes/dashboard/fichas/[id]'
import { DocentesListPage } from '@/routes/dashboard/docentes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
})

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<RequireAdmin><DashboardHome /></RequireAdmin>} />
              {/* Fichas de cata: ADMIN + DOCENTE (el layout ya exige staff). */}
              <Route path="fichas" element={<FichasListPage />} />
              <Route path="fichas/nueva" element={<FichaNewPage />} />
              <Route path="fichas/:id" element={<FichaEditPage />} />
              {/* Secciones solo ADMIN. */}
              <Route path="docentes" element={<RequireAdmin><DocentesListPage /></RequireAdmin>} />
              <Route path="noticias" element={<RequireAdmin><NoticiasListPage /></RequireAdmin>} />
              <Route path="noticias/nueva" element={<RequireAdmin><NoticiaNewPage /></RequireAdmin>} />
              <Route path="noticias/:id" element={<RequireAdmin><NoticiaEditPage /></RequireAdmin>} />
              <Route path="usuarios" element={<RequireAdmin><UsuariosListPage /></RequireAdmin>} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
